import { MongoRuleId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { RulesTriggeredEventData } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { useRequiredContext } from "@redotech/react-util/context";
import ChevronDown from "@redotech/redo-icons/chevron-down.svg";
import ChevronUp from "@redotech/redo-icons/chevron-up.svg";
import LightningIcon from "@redotech/redo-icons/lightning-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RuleTriggerType } from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EventTimestamp } from "../event-timestamp";
import * as styles from "../timeline-event.module.css";
import * as rulesStyles from "./rules-triggered-event.module.css";

export function getTriggerTypeLabel(triggerType: RuleTriggerType): string {
  switch (triggerType) {
    case RuleTriggerType.CONVERSATION_CREATED:
      return "Conversation created";
    case RuleTriggerType.CONVERSATION_MESSAGE_CREATED:
      return "Conversation message created";
    case RuleTriggerType.MERCHANT_MESSAGE_CREATED:
      return "Merchant message created";
    case RuleTriggerType.MESSAGE_RECEIVED:
      return "Message received";
    case RuleTriggerType.CONVERSATION_STATUS_CHANGED:
      return "Status change";
    case RuleTriggerType.TAG_ADDED:
      return "Tag added";
    case RuleTriggerType.ASSIGNMENT_CHANGED:
      return "Assignment change";
    case RuleTriggerType.CHAT_CONVERTED_TO_EMAIL:
      return "Chat converted to email";
    case RuleTriggerType.CONVERSATION_READ:
      return "Conversation read";
    case RuleTriggerType.THREAD_CREATED:
      return "Thread created";
    case RuleTriggerType.CONVERSATION_PRIORITY_CHANGED:
      return "Priority change";
    case RuleTriggerType.TAG_REMOVED:
      return "Tag removed";
    default:
      assertNever(triggerType);
  }
}

export const RulesTriggeredEvent = memo(function RulesTriggeredEvent({
  event,
  data,
}: {
  event: SupportTicketTimelineEventResponse;
  data: RulesTriggeredEventData;
}) {
  const count = data.rules.length;
  const triggerLabel = getTriggerTypeLabel(data.triggerType as RuleTriggerType);
  const description = `${triggerLabel} triggered ${count} rule${count !== 1 ? "s" : ""}`;
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Flex align="flex-start" className={styles.card} dir="row" gap="sm">
      <Flex
        align="center"
        className={`${styles.icon} ${styles.iconWarning}`}
        justify="center"
      >
        <LightningIcon />
      </Flex>
      <Flex className={styles.content} dir="column" gap="xxs">
        <Flex
          align="center"
          className={rulesStyles.clickable}
          justify="space-between"
          onClick={handleToggle}
        >
          <Text fontSize="xs">{description}</Text>
          <Flex align="center" className={rulesStyles.chevron}>
            {expanded ? <ChevronUp /> : <ChevronDown />}
          </Flex>
        </Flex>
        {expanded && <RulesExpandedList eventData={data} />}
        <EventTimestamp event={event} />
      </Flex>
    </Flex>
  );
});

const RulesExpandedList = memo(function RulesExpandedList({
  eventData,
}: {
  eventData: RulesTriggeredEventData;
}) {
  const navigate = useNavigate();
  const team = useRequiredContext(TeamContext);
  const { supportTicketId } = useParams<{ supportTicketId: string }>();

  const handleViewRule = useCallback(
    (ruleId: MongoRuleId) => {
      navigate(
        `/stores/${team._id}/settings/rules/edit/${MongoRuleId.toString(ruleId)}`,
      );
    },
    [navigate, team._id],
  );

  const handleViewExecution = useCallback(
    (workflowId: string) => {
      navigate(
        `/stores/${team._id}/support-v2/${supportTicketId}/rule-execution/${workflowId}`,
      );
    },
    [navigate, team._id, supportTicketId],
  );

  return (
    <Flex dir="column" gap="xxs">
      {eventData.rules.map((rule) => (
        <Flex
          align="center"
          className={rulesStyles.ruleRow}
          gap="xs"
          justify="space-between"
          key={rule.ruleId}
        >
          <Text fontSize="xxs" textColor="secondary">
            {rule.ruleName ?? `Rule ...${rule.ruleId.slice(-6)}`}
          </Text>
          <Flex gap="xxs">
            <RedoButton
              hierarchy="tertiary"
              onClick={() => handleViewExecution(rule.workflowId)}
              size="xs"
              text="View execution"
            />
            <RedoButton
              hierarchy="tertiary"
              onClick={() => handleViewRule(rule.ruleId)}
              size="xs"
              text="View rule"
            />
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
});
